import {
  Box,
  Stack,
  Typography,
  FormControlLabel,
  Checkbox,
  Divider,
  Button,
  Dialog,
} from '@mui/material';
import type { InventoryResource } from '@repo/api-gw-sdk';
import Image from 'next/image';
import { useState, useMemo } from 'react';

import { Loader } from '@/components/layout/loading';
import { Icon } from '@/components/shared/icon';
import { useDAL } from '@/contexts/useDAL';
import { ApplicationsLogosWithAliases } from '@/data/inventory/data';

function appsState(
  entities: InventoryResource[],
  knownApps: string[]
): Map<string, { intermediate: boolean; checked: boolean }> {
  const appsSelected = knownApps.map((app) => {
    const allEntitiesHaveApp = entities.every((entity) =>
      entity.classifications?.appsDetails?.apps?.some((x) => x.name === app)
    );
    const someEntitiesHaveApp = entities.some((entity) =>
      entity.classifications?.appsDetails?.apps?.some((x) => x.name === app)
    );
    return {
      intermediate: !allEntitiesHaveApp && someEntitiesHaveApp,
      checked: allEntitiesHaveApp,
      app,
    };
  });
  return new Map(appsSelected.map((x) => [x.app, x]));
}

export function AppsEditor({
  open,
  onClose,
  entities,
}: {
  open: boolean;
  onClose: () => void;
  entities: InventoryResource[];
}) {
  const dal = useDAL();
  const { data: knownAppsResponse, isLoading } =
    dal.inventory.apps.useKnownList();
  const knownApps = useMemo(
    () => knownAppsResponse?.apps ?? [],
    [knownAppsResponse]
  );

  const [modifications, setModifications] = useState(
    new Map<string, { intermediate: boolean; checked: boolean }>()
  );
  const [updating, setUpdating] = useState(false);

  const applications = useMemo(() => {
    const state = appsState(entities, knownApps);
    for (const [key, value] of modifications) {
      state.set(key, value);
    }
    return state;
  }, [entities, knownApps, modifications]);

  const onSave = () => {
    setUpdating(true);
    const selected = [...applications.entries()]
      .filter(([, state]) => state.checked)
      .map(([key]) => key);

    void dal.inventory.apps
      .override(
        entities.map((e) => e.id),
        selected
      )
      .finally(() => {
        setUpdating(false);
        onClose();
      });
  };

  return (
    <>
      <Dialog open={open}>
        <Box
          sx={{
            m: 0,
            p: 0,
          }}
        >
          <Stack className='h-full'>
            <Stack
              justifyContent='space-between'
              direction='row'
              alignItems='center'
              padding='16px 24px'
            >
              <Stack alignItems='center' direction='row'>
                <div className='flex flex-col'>
                  <Typography className='font-semibold'>
                    Override applications
                  </Typography>
                </div>
              </Stack>
              <div className='flex gap-4'>
                <Icon
                  onClick={onClose}
                  iconClass='material-symbols-close-rounded'
                />
              </div>
            </Stack>
            <Box
              sx={{
                paddingLeft: '40px',
                paddingRight: '40px',
                maxWidth: '630px',
              }}
            >
              {' '}
              <Typography sx={{ lineHeight: '20px' }} className='font-light'>
                {`Eon won't automatically classify applications for the selected resources. This could change the applied backup policies and controls.`}
              </Typography>
            </Box>
            <Divider sx={{ marginTop: '24px', marginBottom: '12px' }} />
            <div className='mt-4'>
              <Box
                sx={{
                  overflowY: 'auto',
                  maxHeight: '312px',
                  marginLeft: '16px',
                }}
              >
                {isLoading ? (
                  <Loader variant='inline' />
                ) : (
                  knownApps.map((x: string) => (
                    <Stack
                      key={x}
                      direction='row'
                      sx={{ height: '48px' }}
                      data-testid={
                        ApplicationsLogosWithAliases[x]
                          ? undefined
                          : `app-missing-logo-${x}`
                      }
                    >
                      <FormControlLabel
                        title={x}
                        className='w-full'
                        control={
                          <Stack direction={'row-reverse'} alignItems='center'>
                            {ApplicationsLogosWithAliases[x] && (
                              <Box
                                data-testid={`app-has-logo-${x}`}
                                sx={{
                                  width: '30px',
                                  height: '30px',
                                  marginRight: '12px',
                                }}
                              >
                                <Image
                                  alt={x}
                                  src={ApplicationsLogosWithAliases[x]}
                                />
                              </Box>
                            )}
                            <Checkbox
                              sx={{ padding: '12px 12px 12px 30px' }}
                              size='small'
                              indeterminate={
                                applications.get(x)?.intermediate ?? false
                              }
                              checked={applications.get(x)?.checked ?? false}
                              onChange={() => {
                                const state = applications.get(x);
                                if (!state) {
                                  return;
                                }
                                const checked = !state.checked;
                                const intermediate = false;
                                setModifications(
                                  new Map([
                                    ...modifications,
                                    [x, { checked, intermediate }],
                                  ])
                                );
                              }}
                            />
                          </Stack>
                        }
                        label={x}
                      />
                    </Stack>
                  ))
                )}
              </Box>
            </div>
            {/* Create two buttons at the bottom, aligned to the right */}
            <Stack
              justifyContent='flex-end'
              direction='row'
              padding='16px 24px'
            >
              <Box>
                <Button onClick={onClose}>
                  <Typography>Cancel</Typography>
                </Button>
              </Box>
              <Box marginLeft='12px'>
                <Button
                  onClick={onSave}
                  variant='outlined'
                  disabled={
                    [...applications.values()].some((x) => x.intermediate) ||
                    updating
                  }
                >
                  {updating ? (
                    <Loader variant='inline' />
                  ) : (
                    <Typography>Override</Typography>
                  )}
                </Button>
              </Box>
            </Stack>
          </Stack>
        </Box>
      </Dialog>
    </>
  );
}
